/**
 * `Quality` in the installed PWA — **F17 on the Entry/Sandbox tier**.
 *
 * ⚠️ **This is an architectural absence, not a backlog item, and the difference is the whole
 * screen.** [11 §6](../../../../docs/platform/11-technical-architecture.md) makes scheduled and
 * continuous monitoring unavailable locally *by design*: a device that only runs when somebody opens
 * it cannot accumulate the periodic history a trend is made of, and F17's entire value is the trend
 * rather than the instant score. Building a local scorecard that scored whenever the tab was open
 * would produce a chart of when its owner happened to visit.
 *
 * ⚠️ **So it says that, with a number, and offers the next step** — ADR-041's trust-graduation
 * pattern, and the same rule the local tier follows everywhere else: *never a bare "feature
 * disabled"*. The quantity comes from what is actually in this device's store, so the sentence is
 * about their data rather than about our roadmap.
 *
 * ⚠️ **What CAN be answered locally is answered**, rather than withheld to make the upgrade look
 * better: the current duplication rate over the last local run is a real, present-tense score, and
 * showing it is what makes the missing half legible as *history* rather than as *the feature*.
 */
import { useEffect, useState } from 'react';
import { Callout, Card, EmptyState, FormulaBlock, HelpText, PageHeader, Skeleton } from '@dedupr/ui';
import { FORMULAS, MIN_POPULATION, scoreObject, type Score } from '@dedupr/quality';
import { GraduationPrompt } from '../GraduationPrompt.js';
import { openLocalStore } from '../local/store.js';

export function LocalQuality() {
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'empty' } | { status: 'ready'; scores: Score[]; records: number }
  >({ status: 'loading' });
  const [graduation, setGraduation] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const store = await openLocalStore();
      const run = await store.latestRun();
      if (!run || run.stage !== 'complete') return null;
      const [records, entities] = await Promise.all([store.allRecords(), store.entitiesFor(run.id)]);
      /* ⚠️ Members of MULTI-member clusters only — Q33's formula verbatim. Counting every clustered
         record would include singletons and turn a clean dataset into a 100% duplication rate. */
      const clustered = entities
        .filter((entity) => (entity.members?.length ?? 0) > 1)
        .reduce((sum, entity) => sum + (entity.members?.length ?? 0), 0);
      return { run, records: records.length, clustered };
    })()
      .then((result) => {
        if (cancelled) return;
        if (!result) {
          setState({ status: 'empty' });
          return;
        }
        const { run } = result;
        const scores = scoreObject({
          run_id: run.id,
          /* ⚠️ No field profiles are persisted locally, so the field dimensions return
             `not_applicable` with their own reason rather than a fabricated 100%. */
          fields: [],
          identity_fields: [],
          live_records: result.records,
          clustered_records: result.clustered,
          /* ⚠️ A CSV carries no modification time. Not-knowable, not zero. */
          stale_records: null,
          staleness_window_days: 180,
          reference_entities: null,
          missing_entities: null,
        });
        setState({ status: 'ready', scores, records: result.records });
      })
      .catch(() => setState({ status: 'empty' }));
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'loading') return <Skeleton />;

  const duplication =
    state.status === 'ready' ? state.scores.find((s) => s.dimension === 'duplication_rate') : undefined;

  return (
    <div className="quality">
      <PageHeader
        title="Data quality"
        description="What this device can measure about the file you last compared, and what needs the cloud."
      />

      {state.status === 'empty' ? (
        <EmptyState
          title="Nothing has been compared yet"
          description="Add a file on Sources and run a comparison. The duplication rate for that run appears here."
        />
      ) : (
        <Card headingLevel={2} title="This run">
          {duplication?.state === 'scored' && duplication.value !== null ? (
            <p>
              <strong>{duplication.value}%</strong> of the {state.records.toLocaleString()} records in
              your last run are in a group with at least one other record.
            </p>
          ) : (
            <p>
              {duplication?.reason ??
                `Only ${state.records.toLocaleString()} records — a rate below ${MIN_POPULATION} is noise rather than a measurement, so no score is given.`}
            </p>
          )}
          <FormulaBlock label="How the duplication rate is computed">{FORMULAS.duplication_rate}</FormulaBlock>
        </Card>
      )}

      <Card headingLevel={2} title="What this device cannot measure">
        <Callout tone="info" title="A trend needs periods, and a device only runs when you open it">
          The five other quality dimensions — completeness, format conformity, staleness, PII coverage
          and cross-system gaps — are measured over profiles and multiple connected systems, and every
          one of them is a comparison over time. Locally there is nothing scoring while the app is
          shut, so a chart here would show when you happened to visit rather than how your data
          changed. This is by architecture rather than a missing feature.
        </Callout>
        <HelpText>
          <button type="button" className="quality__link" onClick={() => setGraduation(true)}>
            What would running in the cloud add?
          </button>
        </HelpText>
      </Card>

      {graduation ? (
        <GraduationPrompt
          ceiling="Quality is measured over time, and this device only measures when it is open"
          /* ⚠️ ADR-041's required QUANTITY, computed from their own data rather than phrased vaguely. */
          missing={
            state.status === 'ready'
              ? `Five of the six quality dimensions are unmeasured for your ${state.records.toLocaleString()} records — completeness, format conformity, staleness, PII coverage and cross-system gaps — and none of the six has a history, so nobody can tell whether last month was better.`
              : 'All six quality dimensions, and the history that turns any of them into a trend, are unmeasured on this device.'
          }
          because="Scoring runs on a schedule against staged data, and a browser tab is only running while you have it open — so there is no series to draw. Cloud processing scores every hour whether anybody is looking."
          onDismiss={() => setGraduation(false)}
        />
      ) : null}
    </div>
  );
}
